// src/HomePage.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  ShoppingCart,
  User,
  X,
  Filter,
  ShoppingBag,
  LogOut,
  LayoutDashboard,
} from 'lucide-react';
import API_BASE from './apiBase';
import QaErrorRegion from './components/QaErrorRegion';

import placeholderImg from './img/thumbnail_product.png';
const PLACEHOLDER_IMG = placeholderImg;

// FE-only: block admins from purchasing 
const BLOCK_ADMIN_PURCHASES = true;
const LOW_STOCK = 5;

// --- helpers ----------------------------------------------------------------
function truthy(v) {
  return ['1', 'true', 'yes', 'on'].includes(String(v ?? '').toLowerCase());
}
function parseUrlParams(searchParams) {
  const brands = (searchParams.get('brands') || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const flavors = (searchParams.get('flavors') || '')
    .split(',').map(s => s.trim()).filter(Boolean);

  const sugarFreeOnly = truthy(searchParams.get('sugar_free'));
  const vegan        = truthy(searchParams.get('vegan'));
  const glutenFree   = truthy(searchParams.get('gluten_free') || searchParams.get('glutenFree'));
  const allNatural   = truthy(searchParams.get('all_natural') || searchParams.get('allNatural'));
  const q = (searchParams.get('q') || '').trim();

  return { brands, flavors, sugarFreeOnly, vegan, glutenFree, allNatural, q };
}
function shallowEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}
function slug(v = '') {
  return String(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}


export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  // ---------- Auth (header) ----------
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isAdmin = !!user && (user.role === 'admin' || user.role === 'staff');
  const canBuy = !isAdmin || !BLOCK_ADMIN_PURCHASES;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (!alive) return;
        setUser(res.ok ? await res.json() : null);
      } catch {
        if (alive) setUser(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  async function handleLogout() {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    setUser(null);
    setMenuOpen(false);
    window.location.reload();
  }

  // ---------- Cart badge ----------
  const [cartCount, setCartCount] = useState(0);
  useEffect(() => {
    if (!canBuy) return; // don't fetch cart for admins when blocked
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/cart/count`, { credentials: 'include' });
        const data = await res.json();
        if (alive) setCartCount(Number(data.count || 0));
      } catch {}
    })();
    return () => { alive = false; };
  }, [canBuy]);

  // ---------- Filters & search (URL-synced) ----------
  const initialFromUrl = parseUrlParams(searchParams);
  const [filters, setFilters] = useState(() => ({
    brands: initialFromUrl.brands,
    flavors: initialFromUrl.flavors,
    sugarFreeOnly: initialFromUrl.sugarFreeOnly,
    vegan: initialFromUrl.vegan,
    glutenFree: initialFromUrl.glutenFree,
    allNatural: initialFromUrl.allNatural,
  }));
  const [searchInput, setSearchInput] = useState(() => initialFromUrl.q);
  const [searchTerm, setSearchTerm] = useState(() => initialFromUrl.q);

  // Reader effect: URL -> state (only change state if different)
  useEffect(() => {
    const parsed = parseUrlParams(searchParams);
    const nextFilters = {
      brands: parsed.brands,
      flavors: parsed.flavors,
      sugarFreeOnly: parsed.sugarFreeOnly,
      vegan: parsed.vegan,
      glutenFree: parsed.glutenFree,
      allNatural: parsed.allNatural,
    };
    setFilters(prev => (shallowEqual(prev, nextFilters) ? prev : nextFilters));
    setSearchInput(prev => (prev === parsed.q ? prev : parsed.q));
    setSearchTerm(prev => (prev === parsed.q ? prev : parsed.q));
  }, [searchParams]);

  // Writer: push URL only on user actions (no writer effect)
  function pushFiltersToUrl(nextFilters, nextSearchTerm) {
    const params = new URLSearchParams();
    if (nextFilters.brands.length)   params.set('brands', nextFilters.brands.join(','));
    if (nextFilters.flavors.length)  params.set('flavors', nextFilters.flavors.join(','));
    if (nextFilters.sugarFreeOnly)   params.set('sugar_free', '1');
    if (nextFilters.vegan)           params.set('vegan', '1');
    if (nextFilters.glutenFree)      params.set('gluten_free', '1');
    if (nextFilters.allNatural)      params.set('all_natural', '1');
    if ((nextSearchTerm || '').trim()) params.set('q', nextSearchTerm.trim());
    setSearchParams(params);
  }

  // ---------- Products (server-side filters) ----------
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [prodError, setProdError] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState({}); // product.id -> variant.id

  function buildProductsUrl({ forFacets = false } = {}) {
    const params = new URLSearchParams();

    // For facet lists we only respect the search term (so other options stay visible)
    if (!forFacets) {
      if (filters.brands.length)   params.set('brands', filters.brands.join(','));
      if (filters.flavors.length)  params.set('flavors', filters.flavors.join(','));
      if (filters.sugarFreeOnly)   params.set('sugar_free', '1');
      if (filters.vegan)           params.set('vegan', '1');
      if (filters.glutenFree)      params.set('gluten_free', '1');
      if (filters.allNatural)      params.set('all_natural', '1');
    }
    if (searchTerm) params.set('q', searchTerm);

    const qs = params.toString();
    return qs ? `${API_BASE}/api/products?${qs}` : `${API_BASE}/api/products`;
  }

  async function fetchProducts() {
    setLoadingProducts(true);
    setProdError('');
    try {
      const res = await fetch(buildProductsUrl(), { credentials: 'include' });
      const data = await res.json();

      // Normalize flags to real booleans so React never renders "0"
      const list = (Array.isArray(data) ? data : []).map(p => ({
        ...p,
        is_sugar_free:  !!p.is_sugar_free,
        is_vegan:       !!p.is_vegan,
        is_gluten_free: !!p.is_gluten_free,
        is_all_natural: !!p.is_all_natural,
      }));
      setProducts(list);

      // default to first variant per product
      const map = {};
      for (const p of list) if (p.variants?.length) map[p.id] = p.variants[0].id;
      setSelectedVariantId(map);
    } catch {
      setProdError('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchTerm]);

  function currentVariant(p) {
    const vid = selectedVariantId[p.id];
    return p.variants?.find(v => v.id === vid) || p.variants?.[0] || null;
  }

  function fmtPrice(amount, currency = 'CAD') {
    const n = Number(amount ?? 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  }

  async function addToCart(product) {
    try {
      const variant = currentVariant(product);
      const vQty = Number(variant?.stock_qty || 0);
      if (!variant || vQty <= 0 || !canBuy) return; // extra FE guard

      const res = await fetch(`${API_BASE}/api/cart/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: product.id,
          product_variant_id: variant.id,
          quantity: 1,
        }),
      });
      const data = await res.json();
      if (res.ok) setCartCount(Number(data.count || cartCount + 1));
    } catch {}
  }

  // ---------- FACET OPTIONS (brand/flavor lists that don't disappear) ----------
  const [facetBrands, setFacetBrands] = useState([]);
  const [facetFlavors, setFacetFlavors] = useState([]);

  // Build facets based on search term only (union of possibilities)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(buildProductsUrl({ forFacets: true }), { credentials: 'include' });
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];

        const brands = Array.from(new Set(list.map(p => p.brand).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        const flavors = Array.from(new Set(list.flatMap(p => (p.flavors || [])))).sort((a, b) => a.localeCompare(b));

        if (alive) {
          setFacetBrands(brands);
          setFacetFlavors(flavors);
        }
      } catch {
        if (alive) {
          setFacetBrands([]);
          setFacetFlavors([]);
        }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // re-evaluate options when the user changes the text search

  // Also ensure any currently-checked brand/flavor stays visible even if not in facets (edge case)
  const brandOptions = useMemo(() => {
    const set = new Set(facetBrands);
    for (const b of filters.brands) set.add(b);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [facetBrands, filters.brands]);

  const flavorOptions = useMemo(() => {
    const set = new Set(facetFlavors);
    for (const f of filters.flavors) set.add(f);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [facetFlavors, filters.flavors]);

  // ---------- Filter handlers ----------
  function toggleArrayFilter(key, value) {
    setFilters(prev => {
      const arr = prev[key];
      const next = arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value];
      const nf = { ...prev, [key]: next };
      pushFiltersToUrl(nf, searchTerm);
      return nf;
    });
  }
  function toggleSugarFree(checked) {
    setFilters(prev => {
      const nf = { ...prev, sugarFreeOnly: checked };
      pushFiltersToUrl(nf, searchTerm);
      return nf;
    });
  }
  function toggleClaim(key, checked) {
    setFilters(prev => {
      const nf = { ...prev, [key]: checked };
      pushFiltersToUrl(nf, searchTerm);
      return nf;
    });
  }
  function clearFilters() {
    const nf = {
      brands: [],
      flavors: [],
      sugarFreeOnly: false,
      vegan: false,
      glutenFree: false,
      allNatural: false,
    };
    setFilters(nf);
    pushFiltersToUrl(nf, searchTerm);
  }

  // ---------- Autocomplete ----------
  const searchRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isSugOpen, setIsSugOpen] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    const term = searchInput.trim();

    if (!term) {
      setSuggestions([]);
      setIsSugOpen(false);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/products/search?query=${encodeURIComponent(term)}`,
          { signal: ctrl.signal }
        );
        const data = await res.json();
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setIsSugOpen(true);
      } catch {}
    }, 250);

    return () => { clearTimeout(t); ctrl.abort(); };
  }, [searchInput]);

  function applySearch(q) {
    const cleaned = (q || '').trim();
    setSearchInput(cleaned);
    setSearchTerm(cleaned);
    pushFiltersToUrl(filters, cleaned);
    setSuggestions([]);
    setIsSugOpen(false);
  }

  // -------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50" data-qa-root>
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50" data-qa="home-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent" data-qa="brand-logo">
                SodaSips
              </div>
            </div>

            {/* Search Bar + suggestions */}
            <div className="flex-1 max-w-2xl mx-8" data-qa="home-search">
              <div className="relative">
                <input
                  ref={searchRef}
                  data-qa="search-input"
                  type="text"
                  value={searchInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearchInput(v);
                    setIsSugOpen(!!v.trim());
                  }}
                  onFocus={() => setIsSugOpen(!!searchInput.trim())}
                  onBlur={() => setTimeout(() => setIsSugOpen(false), 120)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applySearch(searchInput);
                      searchRef.current?.blur();
                    } else if (e.key === 'Escape') {
                      setIsSugOpen(false);
                      setSuggestions([]);
                      searchRef.current?.blur();
                    }
                  }}
                  placeholder="Search for sodas, brands, flavors..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-full focus:border-orange-500 focus:outline-none transition-colors duration-200 text-gray-700"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />

                {/* Suggestion dropdown */}
                {isSugOpen && suggestions.length > 0 && (
                  <div
                    className="absolute mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
                    data-qa="search-suggestions"
                    role="listbox"
                  >
                    {suggestions.map((sug, idx) => (
                      <button
                        key={`${sug.type}-${sug.value}-${idx}`}
                        type="button"
                        data-qa="search-suggestion"
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          applySearch(sug.value);
                        }}
                      >
                        <span className="text-xs uppercase text-gray-500 w-16">{sug.type}</span>
                        <span className="text-gray-800">{sug.value}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side Icons */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Admin shortcut (header) */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  title="Go to Admin Dashboard"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              )}

              {!user ? (
                <Link
                  to="/signin"
                  data-qa="nav-signin"
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-orange-500 transition-colors duration-200"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:block">Sign In</span>
                </Link>
              ) : (
                <div className="relative" ref={menuRef} data-qa="header-user-menu">
                  <button
                    type="button"
                    data-qa="user-menu-button"
                    onClick={() => setMenuOpen((s) => !s)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-orange-500 transition-colors duration-200"
                  >
                    <User className="w-5 h-5" />
                    <span className="hidden sm:block">Hello, {user.first_name}</span>
                    <svg className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.188l3.71-3.958a.75.75 0 111.08 1.04l-4.24 4.52a.75.75 0 01-1.08 0l-4.24-4.52a.75.75 0 01.02-1.06z"/>
                    </svg>
                  </button>

                  {menuOpen && (
                    <div
                      role="menu"
                      data-qa="user-menu-dropdown"
                      className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
                    >
                      {isAdmin ? (
                        <>
                          <Link
                            role="menuitem"
                            to="/admin"
                            onClick={() => setMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50"
                          >
                            <LayoutDashboard className="w-4 h-4" />
                            <span>Admin Dashboard</span>
                          </Link>
                          <div className="h-px bg-gray-100 my-1" />
                          <button
                            role="menuitem"
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Logout</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            role="menuitem"
                            data-qa="user-menu-profile"
                            to="/userprofile"
                            onClick={() => setMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50"
                          >
                            <User className="w-4 h-4" />
                            <span>My Profile</span>
                          </Link>
                          <div className="h-px bg-gray-100 my-1" />
                          <button
                            role="menuitem"
                            data-qa="user-menu-logout"
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Logout</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {canBuy && (
                <Link
                  to="/cart"
                  data-qa="nav-cart-link"
                  className="relative p-2 text-gray-700 hover:text-orange-500 transition-colors duration-200"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {cartCount > 0 && (
                    <span
                      data-qa="nav-cart-count"
                      className="absolute -top-2 -right-2 bg-orange-500 text-white rounded-full min-w-5 h-5 px-1 text-[11px] flex items-center justify-center"
                    >
                      {cartCount}
                    </span>
                  )}
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Admin banner explaining purchase lock */}
      {isAdmin && BLOCK_ADMIN_PURCHASES && (
        <div className="max-w-7xl mx-auto px-4">
          <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-800 px-4 py-2 text-sm">
            You’re signed in with an admin account. Purchasing is disabled on the storefront.
          </div>
        </div>
      )}

      {/* Main Content */}
      <main data-qa-page="home">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-8">
            {/* Filter Sidebar */}
            <aside
              className={`transition-all duration-300 ${isFilterOpen ? 'w-80' : 'w-16'}`}
              data-qa="filters"
            >
              <div className={`bg-white rounded-xl shadow-lg sticky top-24 ${isFilterOpen ? 'p-6' : 'p-3'}`}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-lg font-semibold text-gray-800 ${isFilterOpen ? 'block' : 'hidden'}`}>
                    Filters
                  </h2>
                  <button
                    data-qa="filters-toggle"
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    aria-label={isFilterOpen ? 'Collapse filters' : 'Expand filters'}
                  >
                    {isFilterOpen ? <X className="w-5 h-5" /> : <Filter className="w-5 h-5" />}
                  </button>
                </div>

                {isFilterOpen && (
                  <div className="space-y-6">
                    {/* Brand Filter */}
                    <div data-qa="filter-brand">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-800">Brand</h3>
                        {filters.brands.length > 0 && (
                          <button
                            data-qa="filter-brand-clear"
                            onClick={() => {
                              const nf = { ...filters, brands: [] };
                              setFilters(nf);
                              pushFiltersToUrl(nf, searchTerm);
                            }}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="mt-2 space-y-2">
                        {brandOptions.map((b) => (
                          <label key={b} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              data-qa={`filter-brand-${slug(b)}`}
                              className="text-orange-500 focus:ring-orange-500 rounded"
                              checked={filters.brands.includes(b)}
                              onChange={() => toggleArrayFilter('brands', b)}
                            />
                            <span className="text-sm text-gray-700">{b}</span>
                          </label>
                        ))}
                        {!brandOptions.length && (
                          <div className="text-sm text-gray-400">No brands</div>
                        )}
                      </div>
                    </div>

                    {/* Dietary & Claims */}
                    <div className="pt-2 border-t border-gray-100" data-qa="filter-claims">
                      <h3 className="font-medium text-gray-800 mb-2">Dietary & Claims</h3>

                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-qa="filter-sugar-free">
                        <input
                          type="checkbox"
                          className="text-orange-500 focus:ring-orange-500 rounded"
                          checked={filters.sugarFreeOnly}
                          onChange={(e) => toggleSugarFree(e.target.checked)}
                        />
                        <span className="font-medium text-gray-800">Sugar-Free</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-qa="filter-vegan">
                        <input
                          type="checkbox"
                          className="text-orange-500 focus:ring-orange-500 rounded"
                          checked={filters.vegan}
                          onChange={(e) => toggleClaim('vegan', e.target.checked)}
                        />
                        <span className="font-medium text-gray-800">Vegan</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-qa="filter-gluten-free">
                        <input
                          type="checkbox"
                          className="text-orange-500 focus:ring-orange-500 rounded"
                          checked={filters.glutenFree}
                          onChange={(e) => toggleClaim('glutenFree', e.target.checked)}
                        />
                        <span className="font-medium text-gray-800">Gluten-Free</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-qa="filter-all-natural">
                        <input
                          type="checkbox"
                          className="text-orange-500 focus:ring-orange-500 rounded"
                          checked={filters.allNatural}
                          onChange={(e) => toggleClaim('allNatural', e.target.checked)}
                        />
                        <span className="font-medium text-gray-800">All-Natural</span>
                      </label>
                    </div>

                    {/* Flavor Filter */}
                    <div className="pt-2 border-t border-gray-100" data-qa="filter-flavor">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-800">Flavor</h3>
                        {filters.flavors.length > 0 && (
                          <button
                            data-qa="filter-flavor-clear"
                            onClick={() => {
                              const nf = { ...filters, flavors: [] };
                              setFilters(nf);
                              pushFiltersToUrl(nf, searchTerm);
                            }}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="mt-2 space-y-2">
                        {flavorOptions.map((fl) => (
                          <label key={fl} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              data-qa={`filter-flavor-${slug(fl)}`}
                              className="text-orange-500 focus:ring-orange-500 rounded"
                              checked={filters.flavors.includes(fl)}
                              onChange={() => toggleArrayFilter('flavors', fl)}
                            />
                            <span className="text-sm text-gray-700">{fl}</span>
                          </label>
                        ))}
                        {!flavorOptions.length && (
                          <div className="text-sm text-gray-400">No flavors</div>
                        )}
                      </div>
                    </div>

                    {(filters.brands.length ||
                      filters.flavors.length ||
                      filters.sugarFreeOnly ||
                      filters.vegan ||
                      filters.glutenFree ||
                      filters.allNatural) ? (
                      <button
                        data-qa="filters-clear-all"
                        onClick={clearFilters}
                        className="w-full mt-2 text-sm text-gray-600 hover:text-gray-800 underline"
                      >
                        Clear all filters
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </aside>

            {/* Product Grid */}
            <section className="flex-1" data-qa="products-section">
              <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-800">Featured Sodas</h1>
                <div className="text-sm text-gray-600" data-qa="products-found">
                  {loadingProducts ? 'Loading…' : `${products.length} products found`}
                </div>
              </div>

              {/* Always-mounted error region */}
              <QaErrorRegion qa="home-products" message={prodError} />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-qa="products-grid">
                {products.map((product) => {
                  // ---- Compute product- and variant-level stock ----
                  const totalProductStock = (product.variants || []).reduce((s, vv) => s + (Number(vv.stock_qty) || 0), 0);
                  const productSoldOut = Boolean(product.is_sold_out ?? (totalProductStock <= 0));

                  const v = currentVariant(product);
                  const vQty = Number(v?.stock_qty || 0);
                  const variantSoldOut = v ? vQty <= 0 : true;
                  const lowLeft = !variantSoldOut && vQty <= LOW_STOCK;

                  const canPurchase = canBuy && !productSoldOut && !variantSoldOut;

                  return (
                    <div
                      key={product.id}
                      data-qa="product-card"
                      data-id={product.id}
                      className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      {/* Image with fallback */}
                      <div className="relative overflow-hidden" data-qa="product-image-wrap">
                        <img
                          data-qa="product-image"
                          src={product.image_url || PLACEHOLDER_IMG}
                          alt={product.name}
                          onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                          className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                        />

                        {/* dietary badges (no stray zeros because flags are booleans) */}
                        <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
                          {!!product.is_sugar_free   && (
                            <div className="bg-green-600 text-white px-2 py-1 rounded-full text-[11px] font-semibold">SUGAR FREE</div>
                          )}
                          {!!product.is_vegan        && (
                            <div className="bg-emerald-500 text-white px-2 py-1 rounded-full text-[11px] font-semibold">VEGAN</div>
                          )}
                          {!!product.is_gluten_free  && (
                            <div className="bg-sky-600 text-white px-2 py-1 rounded-full text-[11px] font-semibold">GLUTEN FREE</div>
                          )}
                          {!!product.is_all_natural  && (
                            <div className="bg-lime-600 text-white px-2 py-1 rounded-full text-[11px] font-semibold">ALL-NATURAL</div>
                          )}
                        </div>

                        {productSoldOut ? (
                          <div className="absolute bottom-3 left-3 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            SOLD OUT
                          </div>
                        ) : null}
                      </div>

                      {/* Details */}
                      <div className="p-6">
                        <div className="mb-2">
                          <span className="text-xs text-gray-500 uppercase tracking-wide" data-qa="product-brand">
                            {product.brand}
                          </span>
                          <h3 className="text-lg font-semibold text-gray-800 group-hover:text-orange-500 transition-colors duration-200" data-qa="product-name">
                            {product.name}
                          </h3>
                        </div>

                        {/* Flavor chips */}
                        {product.flavors?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2" data-qa="product-flavors">
                            {product.flavors.map(fl => (
                              <span key={fl} data-qa={`product-flavor-${slug(fl)}`} className="inline-block text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                {fl}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Description */}
                        <div className="mb-3" data-qa="product-description">
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {product.description}
                          </p>
                        </div>

                        {/* Price + stock chips (variant-aware) */}
                        <div className="mb-4" data-qa="product-price">
                          <div className="flex items-center gap-2">
                            <span className={`text-2xl font-bold ${(productSoldOut || variantSoldOut) ? 'text-gray-400' : 'text-gray-800'}`}>
                              {v ? fmtPrice(v?.price, product.currency || 'CAD') : '—'}
                            </span>
                            {lowLeft ? (
                              <span className="ml-2 text-xs px-2 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-100">
                                Only {vQty} left
                              </span>
                            ) : null}
                            {!productSoldOut && variantSoldOut ? (
                              <span className="ml-2 text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">
                                This variant is sold out
                              </span>
                            ) : null}
                          </div>
                        </div>

                        {/* Pack Size Selection */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Pack Size:</label>
                          <select
                            data-qa="pack-size-select"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            disabled={productSoldOut || !product.variants?.length}
                            value={selectedVariantId[product.id] ?? (product.variants?.[0]?.id ?? '')}
                            onChange={(e) =>
                              setSelectedVariantId((m) => ({ ...m, [product.id]: Number(e.target.value) }))
                            }
                          >
                            {(product.variants || []).map((vv) => {
                              const vvQty = Number(vv.stock_qty || 0);
                              const vvSoldOut = vvQty <= 0;
                              return (
                                <option key={vv.id} value={vv.id} disabled={vvSoldOut}>
                                  {vv.pack_size}{vvSoldOut ? ' — Sold out' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {/* Actions */}
                        <div className="space-y-2">
                          <button
                            data-qa="add-to-cart"
                            onClick={() => canPurchase && addToCart(product)}
                            disabled={!canPurchase}
                            className={`w-full font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2
                              ${!canPurchase
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
                            aria-disabled={!canPurchase}
                            title={!canBuy ? 'Purchasing is disabled for admin accounts' : undefined}
                          >
                            <ShoppingCart className="w-4 h-4" />
                            <span>
                              {productSoldOut
                                ? 'Sold Out'
                                : (variantSoldOut ? 'Variant Sold Out' : 'Add to Cart')}
                            </span>
                          </button>
                          <button
                            data-qa="buy-now"
                            disabled={!canPurchase}
                            className={`w-full font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2
                              ${!canPurchase
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-800 hover:bg-gray-900 text-white'}`}
                            aria-disabled={!canPurchase}
                            title={!canBuy ? 'Purchasing is disabled for admin accounts' : undefined}
                          >
                            <ShoppingBag className="w-4 h-4" />
                            <span>{(!canPurchase) ? (productSoldOut || variantSoldOut ? 'Unavailable' : 'Unavailable for Admin') : 'Buy Now'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white mt-16" data-qa="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-4">
                SodaSips
              </div>
              <p className="text-gray-400">
                Your one-stop shop for the world's best sodas and beverages. Fresh, fizzy, and delivered to your door.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/about" className="hover:text-orange-400 transition-colors duration-200">About Us</a></li>
                <li><a href="/contact" className="hover:text-orange-400 transition-colors duration-200">Contact</a></li>
                <li><a href="/returns" className="hover:text-orange-400 transition-colors duration-200">Returns</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://facebook.com" className="hover:text-orange-400 transition-colors duration-200">Facebook</a></li>
                <li><a href="https://twitter.com" className="hover:text-orange-400 transition-colors duration-200">Twitter</a></li>
                <li><a href="https://instagram.com" className="hover:text-orange-400 transition-colors duration-200">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SodaSips. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
